import { normalizeChord } from './parse';

/**
 * Forma canônica do conteúdo de uma cifra, gravada pela migração e pelo editor:
 *
 * - cabeçalho de seção `!!*Nome*`, sem bangs à direita (o formato antigo exigia `!!!!` para
 *   quebrar linha no HTML; o parser de hoje não precisa) e com o nome padronizado;
 * - estrofes separadas por **uma linha em branco** (antes era `!!` isolado);
 * - repetição sempre no fim da linha (` *2x*`);
 * - acordes entre colchetes normalizados (`bM7` → `BM7`);
 * - sem espaços sobrando, crases em volta da linha nem linhas em branco duplicadas;
 * - toda música abre com uma seção (sem cabeçalho, ganha `!!*Verso*`).
 *
 * Não corrige erro de digitação que exige adivinhar a intenção (colchete sem par, acorde
 * inexistente): isso fica para `findContentIssues` e, na migração, para uma tabela de ajustes
 * revisada à mão.
 */

const HEADER = /^!+\*(.+?)\*!*$/;
const REPEAT_ONLY = /^\*(\d+x)\*$/i;
const REPEAT_END = /\s*\*(\d+x)\*\s*$/i;

const stripAccents = (value: string) =>
  value.normalize('NFD').replace(/\p{Diacritic}/gu, '');

const titleCase = (value: string) =>
  value
    .toLowerCase()
    .replace(
      /(^|[\s-])(\p{L})/gu,
      (_, sep: string, letter: string) => sep + letter.toUpperCase(),
    );

/** `REFRÂO FINAL`, `Reffrão`, `Pré-Refrão`, `PRIMEIRA PARTE` → nomes padronizados. */
export const canonicalSectionName = (name: string) => {
  const trimmed = name.trim().replace(/\s+/g, ' ');
  const key = stripAccents(trimmed).toUpperCase();

  if (/^PRE[- ]?REF\w*$/.test(key)) return 'Pré-refrão';

  const chorus = key.match(/^REF\w*(?: (.+))?$/);
  if (chorus) return chorus[1] ? `Refrão ${titleCase(chorus[1])}` : 'Refrão';

  return titleCase(trimmed);
};

type Item = { kind: 'header' | 'blank' | 'text'; text: string };

/** Conserta colchete duplo e `$` no lugar de `#`, e normaliza os acordes entre colchetes. */
export const cleanChordMarks = (line: string) =>
  line
    .replace(/\[\[([^[\]\n]+)\]\]/g, '[$1]')
    .replace(/\[([^[\]\n]+)\]/g, (_, chords: string) => {
      const typed = chords.replace(/\$/g, '#'); // `F$` → `F#` (shift errado)
      return `[${normalizeChord(typed)}]`;
    });

export type CleanResult = { content: string; changes: string[] };

export const cleanSongContent = (raw: string): CleanResult => {
  const changes = new Set<string>();
  const items: Item[] = [];

  for (const rawLine of raw.split(/\r?\n/)) {
    let line = rawLine.trim();
    if (line !== rawLine.replace(/\r$/, '')) changes.add('espaços nas pontas das linhas');

    if (/^`.*`$/.test(line)) {
      line = line.slice(1, -1).trim();
      changes.add('crases em volta da linha');
    }

    if (!line) {
      items.push({ kind: 'blank', text: '' });
      continue;
    }

    if (/^!+$/.test(line)) {
      items.push({ kind: 'blank', text: '' });
      changes.add('`!!` isolado virou linha em branco');
      continue;
    }

    const header = line.match(HEADER);
    if (header) {
      const name = canonicalSectionName(header[1]!);
      const canonical = `!!*${name}*`;
      if (canonical !== line) changes.add('cabeçalhos padronizados');
      items.push({ kind: 'header', text: canonical });
      continue;
    }

    const repeatOnly = line.match(REPEAT_ONLY);
    if (repeatOnly) {
      const previous = items.at(-1);
      if (previous?.kind === 'text') {
        previous.text = `${previous.text.replace(REPEAT_END, '')} *${repeatOnly[1]!.toLowerCase()}*`;
        changes.add('`*2x*` sozinho foi para o fim da linha anterior');
      }
      continue;
    }

    let text = cleanChordMarks(line);
    text = text.replace(REPEAT_END, (_, times: string) => ` *${times.toLowerCase()}*`);
    if (text !== line) changes.add('acordes e repetições normalizados');
    items.push({ kind: 'text', text });
  }

  // monta: sem brancos nas pontas nem logo após cabeçalho, no máximo um branco seguido,
  // e uma linha em branco antes de cada seção (menos a primeira)
  const lines: string[] = [];
  let pendingBlank = false;
  let previous: Item | undefined;

  for (const item of items) {
    if (item.kind === 'blank') {
      pendingBlank = true;
      continue;
    }

    if (item.kind === 'header') {
      if (lines.length > 0) lines.push('');
      pendingBlank = false;
    } else if (pendingBlank && previous && previous.kind !== 'header') {
      lines.push('');
    }

    if (item.kind === 'text' && lines.length === 0) {
      lines.push('!!*Verso*');
      changes.add('seção `Verso` adicionada (música sem cabeçalho)');
    }

    lines.push(item.text);
    pendingBlank = false;
    previous = item;
  }

  return { content: lines.join('\n'), changes: [...changes] };
};
